import type { AxiosInstance } from 'axios';
import {
  ClientRateLimiter,
  CSRFTokenManager,
  SecurityLogger,
  sanitizeObject,
} from '../../utils/security';
import { ENV } from '../../config/env';

/**
 * Attach security interceptors (rate limiting, CSRF, sanitization, hardening
 * headers) to an axios client.
 *
 * Unlike PostExchange's stub of the same name, this is the REAL webapp
 * implementation — it wires the existing `ClientRateLimiter` /
 * `CSRFTokenManager` / `SecurityLogger` / `sanitizeObject` helpers from
 * `src/utils/security` into the apiClient's request pipeline:
 *
 *   1. 100 req/min per-endpoint rate-limit (rejects requests that exceed it).
 *   2. `X-Requested-With` + `X-App-Source` hardening headers on every request.
 *   3. CSRF token (`X-CSRF-Token`) on state-changing verbs (POST/PUT/PATCH/DELETE).
 *   4. XSS-defang sanitization of JSON request bodies — strips `<` and `>`
 *      via `sanitizeObject`. Skipped for `FormData` and for the endpoints
 *      listed in `RICH_CONTENT_ENDPOINT_FRAGMENTS` below, which legitimately
 *      carry user-authored HTML / Markdown bodies that the sanitizer would
 *      corrupt.
 *
 * Attached BEFORE `attachAuthInterceptors` so a rate-limit short-circuit
 * happens before any token plumbing runs. Interceptors are attached to the
 * passed-in `client` only — no global axios state is mutated.
 */

// 100 requests per minute per endpoint to mitigate runaway client loops.
const rateLimiter = new ClientRateLimiter(100, 60_000);

/**
 * Endpoints whose bodies legitimately carry HTML / Markdown and must bypass
 * the global angle-bracket-stripping sanitizer. Match is case-sensitive
 * substring against the outgoing request URL — add new fragments here when
 * a new endpoint accepts user-authored HTML in its JSON body.
 */
const RICH_CONTENT_ENDPOINT_FRAGMENTS = [
  '/v1/orders', // POST /v1/orders + PATCH /v1/orders/{id}/content (advertiser_content HTML)
  '/v1/package-orders', // POST /v1/package-orders (per-item content_brief)
  '/v1/support/tickets', // ticket bodies
  '/advertiser-reject-content', // task reject reasons
  '/reject-backlink', // backlink rejection reasons
  '/v1/orders/', // /v1/orders/{id}/reject feedback
];

export function attachSecurityInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use(
    (config) => {
      // Rate-limit per endpoint to mitigate runaway client loops.
      const endpoint = config.url ?? 'unknown';
      if (!rateLimiter.isAllowed(endpoint)) {
        SecurityLogger.log('Rate limit exceeded', { endpoint });
        return Promise.reject(new Error('Too many requests. Please try again later.'));
      }

      // Standard request hardening headers
      config.headers = config.headers ?? {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.headers['X-App-Source'] = ENV.APP_SOURCE;

      // Add CSRF token for state-changing methods
      if (['post', 'put', 'patch', 'delete'].includes((config.method ?? '').toLowerCase())) {
        config.headers['X-CSRF-Token'] = CSRFTokenManager.getToken();
      }

      // Sanitize JSON bodies (skip FormData + rich-content endpoints).
      // sanitizeObject strips `<` and `>` to defang XSS in unknown user input;
      // rich-content endpoints (HTML article bodies, support tickets, etc.)
      // need to bypass it or the payload gets corrupted.
      const url = config.url ?? '';
      const isRichContent = RICH_CONTENT_ENDPOINT_FRAGMENTS.some((frag) => url.includes(frag));
      if (
        config.data &&
        typeof config.data === 'object' &&
        !(config.data instanceof FormData) &&
        !isRichContent
      ) {
        config.data = sanitizeObject(config.data as Record<string, unknown>);
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}
